import { useEffect, useRef, useState } from 'react'
import { getImportSources, importCSV, ImportSource, ImportResult } from '../services/csvImport'
import LoadingSpinner from '../components/LoadingSpinner'

const ImportData = () => {
  const [sources, setSources] = useState<ImportSource[]>([])
  const [loadingSources, setLoadingSources] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [selectedSource, setSelectedSource] = useState<string>('')
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [result, setResult] = useState<ImportResult | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    getImportSources()
      .then((data) => {
        setSources(data)
        if (data.length > 0) setSelectedSource(data[0].key)
      })
      .catch((err) => setLoadError(err.message || 'Error al cargar las fuentes de importación'))
      .finally(() => setLoadingSources(false))
  }, [])

  const selectedSourceConfig = sources.find((s) => s.key === selectedSource)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] || null)
    setResult(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedSource || !file) return

    setUploading(true)
    setResult(null)

    try {
      const response = await importCSV(selectedSource, file)
      setResult(response)

      if (response.success) {
        setFile(null)
        if (fileInputRef.current) fileInputRef.current.value = ''
      }
    } catch (err) {
      setResult({
        success: false,
        error: 'Error inesperado al importar el archivo',
        message: err instanceof Error ? err.message : undefined,
      })
    } finally {
      setUploading(false)
    }
  }

  if (loadingSources) {
    return <LoadingSpinner />
  }

  if (loadError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-2">⚠️</div>
          <p className="text-dashboard-muted">{loadError}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8 max-w-3xl">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-dashboard-text flex items-center">
          <span className="mr-3">📥</span>
          Importar Datos
        </h1>
        <p className="text-dashboard-muted mt-1">
          Sube un CSV para añadir registros a una de las fuentes de datos. Los datos se añaden a los ya
          existentes, no los reemplazan.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="card p-6 space-y-6">
        {/* Paso 1: fuente */}
        <div>
          <label className="block text-sm font-semibold text-dashboard-text mb-2">
            1. Fuente de datos
          </label>
          <select
            className="filter-select w-full"
            value={selectedSource}
            onChange={(e) => {
              setSelectedSource(e.target.value)
              setResult(null)
            }}
          >
            {sources.map((source) => (
              <option key={source.key} value={source.key}>
                {source.label}
              </option>
            ))}
          </select>

          {selectedSourceConfig && (
            <p className="text-xs text-dashboard-muted mt-2">
              El CSV debe tener exactamente estas columnas (en cualquier orden):{' '}
              <span className="font-mono font-semibold text-dashboard-text">
                {selectedSourceConfig.expectedColumns.join(', ')}
              </span>
            </p>
          )}
        </div>

        {/* Paso 2: archivo */}
        <div>
          <label className="block text-sm font-semibold text-dashboard-text mb-2">
            2. Archivo CSV
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="block w-full text-sm text-dashboard-text border border-dashboard-border rounded-lg cursor-pointer bg-white focus:outline-none file:mr-4 file:py-2.5 file:px-4 file:border-0 file:font-medium file:bg-globo-50 file:text-globo-700 hover:file:bg-globo-100"
          />
          <p className="text-xs text-dashboard-muted mt-2">Tamaño máximo: 10MB.</p>
        </div>

        <button type="submit" disabled={!file || uploading} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none">
          {uploading ? 'Importando...' : 'Importar CSV'}
        </button>
      </form>

      {/* Resultado */}
      {result && (
        <div className="card p-6">
          {result.success ? (
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <span className="badge badge-success">Importación completada</span>
              </div>
              <p className="text-dashboard-text">{result.message}</p>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-dashboard-text">{result.totalRows}</p>
                  <p className="text-xs text-dashboard-muted uppercase tracking-wider">Filas leídas</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-dashboard-success">{result.inserted}</p>
                  <p className="text-xs text-dashboard-muted uppercase tracking-wider">Importadas</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-dashboard-error">{result.skipped}</p>
                  <p className="text-xs text-dashboard-muted uppercase tracking-wider">Con errores</p>
                </div>
              </div>

              {result.errors && result.errors.length > 0 && (
                <div className="overflow-x-auto mt-4">
                  <table className="table-modern">
                    <thead>
                      <tr>
                        <th>Fila</th>
                        <th>Errores</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errors.map((rowError) => (
                        <tr key={rowError.row}>
                          <td>{rowError.row}</td>
                          <td>{rowError.errors.join('; ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <span className="badge badge-error">Error</span>
              <p className="text-dashboard-text font-medium">{result.error}</p>
              {result.message && <p className="text-sm text-dashboard-muted">{result.message}</p>}

              {result.missingColumns && result.missingColumns.length > 0 && (
                <div className="text-sm space-y-1">
                  <p className="text-dashboard-muted">
                    Columnas esperadas:{' '}
                    <span className="font-mono">{result.expectedColumns?.join(', ')}</span>
                  </p>
                  <p className="text-dashboard-muted">
                    Columnas encontradas:{' '}
                    <span className="font-mono">{result.foundColumns?.join(', ')}</span>
                  </p>
                  <p className="text-dashboard-error">
                    Faltan: <span className="font-mono">{result.missingColumns.join(', ')}</span>
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ImportData
